/**
 * The viewer API over the Regions runtime (regionsRuntime.ts), kept out of createViewer so it is
 * unit-testable: the Regions methods and events, the aliases that keep scripts written for Focus &
 * Highlight and Annotation Layers working, and the keyframe handler of entity type 'region'.
 */
import type { ResolvedRegion } from '../effects/regions-effect';
import type { RegionInfo } from '../types';
import type { SceneAnimationHandler } from './EntityAnimationSystem';
import type { RegionState, RegionsRuntime } from './regionsRuntime';

/**
 * Regions the legacy layer-group API (setLayerGroupVisible, getLayerGroups...) addresses: the ones
 * in the Layers menu, plus colour-only regions (what annotation layers became, also when their
 * panel was hidden and a host page drove them).
 */
export const isLayerRegion = (r: Pick<ResolvedRegion, 'inLayersMenu' | 'dim'>): boolean => r.inLayersMenu || !r.dim;
export const isLayerRegionState = (r: Pick<RegionState, 'inLayersMenu' | 'dimsOutside'>): boolean => r.inLayersMenu || !r.dimsOutside;

export interface RegionsApi {
    setRegionVisible(id: string, visible: boolean): boolean;
    showOnlyRegion(id: string | null): boolean;
    getVisibleRegions(): string[];
    getRegions(): RegionInfo[];
    /** setRegionActive('focus', ...) */
    setFocusRegionActive(id: string, active: boolean): boolean;
    /** getActiveRegions().focus: the visible regions that dim the rest. */
    getActiveFocusRegions(): string[];
    setLayerGroupVisible(id: string, visible: boolean): boolean;
    showOnlyLayerGroup(id: string | null): boolean;
    setAllLayerGroupsVisible(visible: boolean): void;
    getVisibleLayerGroups(): string[];
    getLayerGroups(): Array<{ id: string; name: string; color: string; section?: string; visible: boolean; area: number }>;
}

export function createRegionsApi(runtime: RegionsRuntime): RegionsApi {
    const layer = (id: unknown) => typeof id === 'string' && runtime.getRegions().some((r) => r.id === id && isLayerRegionState(r));
    return {
        setRegionVisible: (id, visible) => typeof id === 'string' && runtime.setVisible(id, visible === true),
        showOnlyRegion: (id) => runtime.showOnly(typeof id === 'string' ? id : null),
        getVisibleRegions: () => runtime.getVisibleIds(),
        getRegions: () => runtime.getRegions().map((r) => ({
            id: r.id,
            name: r.name,
            color: r.color,
            visible: r.visible,
            inLayersMenu: r.inLayersMenu,
            dimsOutside: r.dimsOutside,
            ...(r.section ? { section: r.section } : {}),
            area: r.area,
        })),
        setFocusRegionActive: (id, active) => typeof id === 'string' && runtime.setVisible(id, active === true),
        // A highlight made from several focus regions that were simply on lists each of their ids, as before.
        getActiveFocusRegions: () => runtime.getRegions().filter((r) => r.dimsOutside && r.visible).flatMap((r) => (r.focusIds.length > 1 ? r.focusIds : [r.id])),
        setLayerGroupVisible: (id, visible) => layer(id) && runtime.setVisible(id, visible === true),
        showOnlyLayerGroup: (id) => {
            if (id !== null && !layer(id)) return false;
            return runtime.showOnly(typeof id === 'string' ? id : null, isLayerRegion);
        },
        setAllLayerGroupsVisible: (visible) => runtime.setAll(visible !== false, isLayerRegion),
        getVisibleLayerGroups: () => runtime.getRegions().filter((r) => isLayerRegionState(r) && r.visible).map((r) => r.id),
        getLayerGroups: () => runtime.getRegions().filter(isLayerRegionState).map((r) => ({
            id: r.id,
            name: r.name,
            color: r.color,
            ...(r.section ? { section: r.section } : {}),
            visible: r.visible,
            area: r.area,
        })),
    };
}

/**
 * Viewer events for region switches: `regionVisibilityChange` ({ id, visible, visibleIds }) once
 * per region that changed, and the legacy `layerVisibilityChange` ({ visible }: the layers shown)
 * on a config edit or when a layer changed. Returns the unsubscribe.
 */
export function bindRegionEvents(runtime: RegionsRuntime, emit: (event: 'regionVisibilityChange' | 'layerVisibilityChange', data: unknown) => void): () => void {
    return runtime.onChange((visibleIds, changes) => {
        for (const change of changes) emit('regionVisibilityChange', { id: change.id, visible: change.visible, visibleIds });
        const layers = runtime.getRegions().filter(isLayerRegionState);
        if (layers.length && (!changes.length || changes.some((c) => layers.some((l) => l.id === c.id)))) {
            emit('layerVisibilityChange', { visible: layers.filter((l) => l.visible).map((l) => l.id) });
        }
    });
}

/**
 * Keyframes of entity type 'region' (entityId = region id): its colour, colour strength and the
 * look of the rest while it dims it, and the transform of a region whose only area is a sphere /
 * box. Uniform writes, never a recompile.
 */
export function regionAnimationHandler(runtime: RegionsRuntime): SceneAnimationHandler {
    return {
        apply: (values, regionId) => runtime.setAnimatedValues(regionId, values),
        readValues: (regionId) => runtime.readAnimatedValues(regionId),
        reset: (props, regionId) => runtime.clearAnimatedValues(regionId, props),
    };
}
